import { Label, Title, Text, Box, Skeleton } from '@nimbus-ds/components';
import { Input as InputNimbus } from '@tiendanube/components';
import { CATALOG_PRODUCT_HASHED_SLUG } from 'App/featuresFlags';
import { useHasTags } from 'App/hooks';
import {
  LanguagesType,
  LanguageInterface,
  DEFAULT_LANGUAGE,
  LanguageInterfaceBoolean,
} from 'App/i18n';
import { AiGeneratedBox, Input, Stack } from 'commons/components';
import GenerateWithAiLink from 'commons/components/GenerateWithAiLink';
import { useTranslationCatalog } from 'domains/Catalog/hooks';
import type { FormSeoNavigationAttributes } from './types';
import SeoGenerationModal from '../SeoGenerationModal';

export type onChangeSeoType = (data: {
  name: string;
  value: LanguageInterface;
  language?: LanguagesType;
}) => void;

export interface FormSeoProps {
  language?: LanguagesType;
  name: LanguageInterface;
  description?: LanguageInterface;
  seoTitle: LanguageInterface;
  seoTitleAI: {
    isGeneratingAI: boolean;
    isGeneratedAI: LanguageInterfaceBoolean;
  };
  seoDescription: LanguageInterface;
  seoDescriptionAI: {
    isGeneratingAI: boolean;
    isGeneratedAI: LanguageInterfaceBoolean;
  };
  onChangeSeo: onChangeSeoType;
  seoUrl: LanguageInterface;
  seoUrlAI?: boolean;
  showAiModal: boolean;
  openAiModal: () => void;
  closeAiModal: () => void;
  showGenerateWithAi: boolean;
  aiNavigationAttributes?: FormSeoNavigationAttributes;
}

function FormSeo({
  language = DEFAULT_LANGUAGE,
  name,
  description,
  onChangeSeo,
  seoDescription,
  seoDescriptionAI,
  seoTitle,
  seoTitleAI,
  seoUrl,
  seoUrlAI,
  showAiModal,
  openAiModal,
  closeAiModal,
  showGenerateWithAi,
  aiNavigationAttributes,
}: Readonly<FormSeoProps>): JSX.Element {
  const t = useTranslationCatalog();
  const [hasCatalogProductHashedSlug] = useHasTags([
    CATALOG_PRODUCT_HASHED_SLUG,
  ]);

  const marginBottom = hasCatalogProductHashedSlug ? 'none' : '2';

  const product = {
    name: name[language],
    description: description?.[language] ?? '',
    lang: language,
  };

  const seoFormUrl = t('products.detail.seo.form.url');

  const handleUseContent = (
    generatedSeoTitle: string,
    generatedSeoDescription: string,
  ) => {
    onChangeSeo({
      name: 'seoTitle',
      value: { ...seoTitle, [language]: generatedSeoTitle },
      language,
    });
    onChangeSeo({
      name: 'seoDescription',
      value: { ...seoDescription, [language]: generatedSeoDescription },
      language,
    });
  };

  const handleOnChange = (data: { value: string; name: string }) => {
    const previousValue =
      data.name === 'seoTitle'
        ? seoTitle
        : data.name === 'seoUrl'
        ? seoUrl
        : seoDescription;

    onChangeSeo({
      name: data.name,
      value: {
        ...previousValue,
        [language]: data.value,
      },
      language,
    });
  };

  return (
    <>
      <Stack column align="stretch">
        <Stack
          column
          align="stretch"
          spacing="tight"
          {...aiNavigationAttributes?.title}
        >
          <Stack spacing="tight">
            <Label htmlFor="input_seoTitle">
              {t('products.detail.seo.form.title')}
            </Label>
            {showGenerateWithAi && (
              <GenerateWithAiLink fontSize="caption" onClick={openAiModal} />
            )}
          </Stack>
          {seoTitleAI.isGeneratingAI ? (
            <Box width="100%">
              <Skeleton width="100%" height="40px" borderRadius="8px" />
            </Box>
          ) : (
            <Input
              type="text"
              placeholder={t('products.detail.seo.form.titlePlaceholder')}
              onChange={handleOnChange}
              name="seoTitle"
              value={seoTitle?.[language] ?? ''}
              helpText={t('products.detail.seo.form.titleHelperText', {
                count: seoTitle?.[language]?.length || 0,
              })}
              helpTextColor="neutral-textLow"
              helpTextFontSize="caption"
              marginBottom={marginBottom}
              maxLength={70}
              disabled={false}
              aiGenerated={seoTitleAI.isGeneratedAI[language]}
            />
          )}
        </Stack>
        <Stack
          column
          align="stretch"
          spacing="tight"
          {...aiNavigationAttributes?.description}
        >
          <Stack spacing="tight">
            <Label htmlFor="input_seoDescription">
              {t('products.detail.seo.form.description')}
            </Label>
            {showGenerateWithAi && (
              <GenerateWithAiLink fontSize="caption" onClick={openAiModal} />
            )}
          </Stack>
          {seoDescriptionAI.isGeneratingAI ? (
            <Box width="100%">
              <Skeleton width="100%" height="96px" borderRadius="8px" />
            </Box>
          ) : (
            <Input
              type="textarea"
              placeholder={t('products.detail.seo.form.descriptionPlaceholder')}
              onChange={handleOnChange}
              name="seoDescription"
              value={seoDescription?.[language] ?? ''}
              lines={3}
              maxLength={320}
              helpText={t('products.detail.seo.form.descriptionHelperText', {
                count: seoDescription?.[language]?.length || 0,
              })}
              helpTextColor="neutral-textLow"
              helpTextFontSize="caption"
              marginBottom={marginBottom}
              disabled={false}
              aiGenerated={seoDescriptionAI.isGeneratedAI[language]}
            />
          )}
        </Stack>
        <Stack.Item {...aiNavigationAttributes?.url}>
          <Box display="flex" flexDirection="column" gap="2">
            {hasCatalogProductHashedSlug && (
              <Box
                marginBottom="2"
                display="flex"
                flexDirection="column"
                gap="2"
              >
                <Title as="h3" fontSize="h5">
                  {seoFormUrl}
                </Title>
                <Text>{t('products.detail.seo.form.urlInfo')}</Text>
              </Box>
            )}
            <AiGeneratedBox
              aiGenerated={seoUrlAI}
              aiGeneratedProps={{ padding: '2' }}
            >
              <InputNimbus
                type="text"
                label={hasCatalogProductHashedSlug ? '' : seoFormUrl}
                aria-label={hasCatalogProductHashedSlug ? seoFormUrl : ''}
                placeholder=""
                onChange={handleOnChange}
                name="seoUrl"
                value={seoUrl?.[language] ?? ''}
                maxLength={156}
                prependLabel={t('products.detail.seo.form.urlPrependLabel')}
                helpText={
                  hasCatalogProductHashedSlug
                    ? t('products.detail.seo.form.urlHelpText')
                    : ''
                }
              />
            </AiGeneratedBox>
          </Box>
        </Stack.Item>
      </Stack>
      <SeoGenerationModal
        show={showAiModal}
        onDismiss={closeAiModal}
        onUseContent={handleUseContent}
        product={product}
      />
    </>
  );
}

export default FormSeo;
